import { formatDate } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { gql, TypedDocumentNode } from 'apollo-angular';
import { OuiAutocomplete, OuiAutocompleteOption } from 'omnium-ui/form-field/public-api';

import { AuthService } from '@lib/services/auth-service.service';
import { QueryManagerService } from 'finzzle-angular-utils';
import { debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { FicheConseilService } from 'src/app/services/fiche-conseil.service';
import { FormConfigService } from 'src/app/services/form-config.service';
import { FieldFormControl } from 'src/fieldFormControl/FieldFormControl';
import {
  Investisseur,
  InvestisseurFilterInput,
  InvestisseursPaginatedCollectionSegment,
  InvestisseurType,
} from '../../../../../fiche-conseil-lib/src/lib/models/generated/graphql';
import { PERSONNE_PHYSIQUE_FEMALE_GENDER_VALUE } from '../../../../../fiche-conseil-lib/src/lib/models/graphqlData';

export const searchInvestisseurFragment = gql`
  fragment searchInvestisseur on Investisseur {
    id
    code
    investisseurEntite {
      displayName
      personnePhysique {
        dateNaissance
        prenom
        nom
        genre
      }
    }
    statutCodeNavigation {
      libelle
      code
    }
    creationDate
  }
`;

const investisseursCouple = gql`
  query investisseurs($filter: InvestisseurFilterInput, $searchText: String!, $consultantId: Long!) {
    investisseursByNamePaginated(skip: 0, take: 20, searchText: $searchText, where: $filter) {
      items {
        ...searchInvestisseur
        personnePhysique {
          id
          # here we need to filter the couple relation with the current consultant on the second person
          # the conjoint can have another consultant, this can lead to an API error: because in this case, the current consultant  does not have access to the conjoint data
          relationCouplePersonnePhysique1(
            where: {
              personnePhysique2: {
                investisseur: {
                  all: {
                    investisseurConsultant: {
                      some: { and: [{ actif: { eq: 1 } }, { consultantId: { eq: $consultantId } }] }
                    }
                  }
                }
              }
            }
          ) {
            personnePhysique2Id
            personnePhysique2 {
              investisseur {
                ...searchInvestisseur
              }
            }
          }
          # here we need to filter the couple relation with the current consultant on the second person
          # the conjoint can have another consultant, this can lead to an API error:  because in this case, the current consultant  does not have access to the conjoint data
          relationCouplePersonnePhysique2(
            where: {
              personnePhysique1: {
                investisseur: {
                  all: {
                    investisseurConsultant: {
                      some: { and: [{ actif: { eq: 1 } }, { consultantId: { eq: $consultantId } }] }
                    }
                  }
                }
              }
            }
          ) {
            personnePhysique1Id
            personnePhysique1 {
              investisseur {
                ...searchInvestisseur
              }
            }
          }
        }
      }
    }
  }
  ${searchInvestisseurFragment}
`;

const investisseursBasic = gql`
  query investisseurs($filter: InvestisseurFilterInput, $searchText: String!) {
    investisseursByNamePaginated(skip: 0, take: 25, searchText: $searchText, where: $filter) {
      items {
        ...searchInvestisseur
      }
    }
  }
  ${searchInvestisseurFragment}
`;
@Component({
  selector: 'app-search-investisseur-autocomplete',
  templateUrl: './search-investisseur-autocomplete.component.html',
  styleUrls: ['./search-investisseur-autocomplete.component.scss'],
})
export class SearchInvestisseurAutocompleteComponent implements AfterViewInit {
  @ViewChild('investisseurAutocomplete') investisseurAutocomplete: OuiAutocomplete<any>;

  @Input() label: string = 'Investisseur';
  @Input() dataKey: string | undefined;
  @Input() initInvestisseur: Investisseur | undefined;
  @Input() isInvestisseurSet: boolean = false;
  @Input() isRequired: boolean = false;
  @Input() isContactStatutExcluded: boolean = false;
  @Input() isClientStatutOnly: boolean = false;
  @Input() typePersonnes: InvestisseurType[] = [InvestisseurType.PersonneMorale, InvestisseurType.PersonnePhysique];
  @Input() fullDisplay: boolean = true;
  @Input() defaultQueryFilter: InvestisseurFilterInput;

  @Output() onInvestisseurIdSelected = new EventEmitter<number | null | undefined>();
  @Output() onInvestisseurPersonneSelected = new EventEmitter<Investisseur | undefined>();
  @Input() set queryMode(value: 'basic' | 'couple') {
    if (value === 'couple') {
      this.investisseurQuery = investisseursCouple;
      this.mode = 'couple';
      return;
    }
    this.investisseurQuery = investisseursBasic;
    this.mode = 'basic';
  }

  @Input() set conjoint(value: Investisseur | undefined) {
    if (value) {
      this.investisseurs = [value];
      this.setOptions();
      if (!this.investisseurControl) {
        this.investisseurControl = new FormControl<number | null>(null);
      }
      this.investisseurControl.setValue(value.id);
      this.onInvestisseurIdSelected.emit(value.id);
      this.onInvestisseurPersonneSelected.emit(value);
    }
  }
  @Input()
  investisseurs: Investisseur[] = [];

  investisseurAutocompleteOptions: OuiAutocompleteOption<number>[] = [];
  investisseurControl: FormControl<number | null>;
  mode: 'basic' | 'couple' = 'basic';
  isInvestisseurLoading: boolean = false;
  investisseurQuery: TypedDocumentNode<unknown, unknown> = investisseursCouple;
  consultantId: number = 0;
  constructor(
    private queryManager: QueryManagerService,
    private ficheConseilService: FicheConseilService,
    private formConfigService: FormConfigService,
    private userService: AuthService
  ) {}

  ngOnInit() {
    this.userService.userHabilitationCheck.subscribe(value => {
      if (value) {
        this.consultantId = this.userService.consultantId ?? 0;
      }
    });
    if (this.dataKey) {
      this.investisseurControl = new FieldFormControl<number | null, Investisseur>({
        formConfigService: this.formConfigService,
        ficheConseilService: this.ficheConseilService,
        dataKey: this.dataKey,
        defaultValue: null,
      });
    } else {
      if (!this.investisseurControl) {
        this.investisseurControl = new FormControl<number | null>(null);
      }
    }
    if (this.initInvestisseur) {
      this.investisseurs = [this.initInvestisseur];
      this.setOptions();
      this.investisseurControl.setValue(this.initInvestisseur.id);
    }
  }

  getErrorTextfield(): import('omnium-ui/form-field/validation-error').OuiValidationError[] {
    if (this.isRequired) {
      return [{ code: 'required', message: 'Champ obligatoire' }];
    } else {
      return [];
    }
  }
  ngOnChanges(changes: SimpleChanges) {
    const statutContactChange = changes['isContactStatutExcluded'];
    if (statutContactChange && statutContactChange.previousValue !== undefined) {
      this.investisseurControl.setValue(null);
      this.onInvestisseurIdSelected.emit(null);
      this.onInvestisseurPersonneSelected.emit(undefined);
    }
  }
  ngAfterViewInit() {
    setTimeout(() => {
      if (this.initInvestisseur) {
        this.investisseurControl.setValue(this.initInvestisseur.id);
      }

      if (this.isRequired) {
        this.investisseurControl.setValidators(Validators.required);
      }

      if (this.initInvestisseur && this.isInvestisseurSet) {
        this.investisseurControl.disable();
      }

      this.initSearchInvestisseurs();
    });
  }

  async initSearchInvestisseurs() {
    this.listenForInvestisseursChanges();

    if (!this.initInvestisseur) {
      this.fetchInvestisseurs();
    }
  }

  setOptions() {
    this.investisseurAutocompleteOptions = this.investisseurs.map(investisseur => ({
      label: this.formatInvestisseurLabel(investisseur),
      value: investisseur.id,
    }));
  }

  listenForInvestisseursChanges() {
    this.investisseurControl.valueChanges.pipe(debounceTime(350), distinctUntilChanged()).subscribe(investisseurId => {
      if (Number.isInteger(investisseurId)) {
        const investisseurFound = this.investisseurs.find(investisseur => investisseur.id == investisseurId);
        if (investisseurFound) {
          this.onInvestisseurIdSelected.emit(investisseurFound.id);
          this.onInvestisseurPersonneSelected.emit(investisseurFound);
        }
      } else {
        if (investisseurId) {
          this.fetchInvestisseurs(investisseurId.toString());
        } else {
          this.fetchInvestisseurs();
        }
        this.onInvestisseurIdSelected.emit(null);
        this.onInvestisseurPersonneSelected.emit(undefined);
      }
    });
  }

  async fetchInvestisseurs(search: string = '') {
    this.isInvestisseurLoading = true;
    const variables: any = { filter: this.buildFilter(), searchText: search, consultantId: this.consultantId };
    if (this.mode === 'couple') {
      variables.consultantId = this.consultantId;
    }
    let investisseurResult = await firstValueFrom(
      this.queryManager.query<{ investisseursByNamePaginated: InvestisseursPaginatedCollectionSegment }>({
        query: this.investisseurQuery,
        variables: variables,
      })
    );
    this.investisseurs = investisseurResult.data.investisseursByNamePaginated.items ?? [];
    this.setOptions();
    this.isInvestisseurLoading = false;
  }

  buildFilter(): InvestisseurFilterInput {
    const personnePhysiqueFilter = { personnePhysique: { any: true } };
    const personneMoraleFilter = { personneMorale: { any: true } };
    //check type investisseur
    const personneFilter: InvestisseurFilterInput = { or: [] };
    if (this.typePersonnes.includes(InvestisseurType.PersonnePhysique)) {
      personneFilter.or!.push(personnePhysiqueFilter);
    }
    if (this.typePersonnes.includes(InvestisseurType.PersonneMorale)) {
      personneFilter.or!.push(personneMoraleFilter);
    }
    const investFilter: InvestisseurFilterInput = { and: [] };
    if (personneFilter.or!.length > 0) {
      investFilter.and!.push(personneFilter);
    }
    // check if contact need to be excluded
    const excludeContactStatutFilter = { statutCode: { neq: 'CONTACT' } };
    if (this.isContactStatutExcluded) {
      investFilter.and!.push(excludeContactStatutFilter);
    }
    // add default filter
    if (this.defaultQueryFilter) {
      investFilter.and!.push(this.defaultQueryFilter);
    }
    return investFilter;
  }
  formatInvestisseurLabel(investisseur: Investisseur) {
    if (investisseur.investisseurEntite) {
      return this.formatLabelFromAtlas(investisseur);
    } else {
      return this.formatLabelFromFicheConseil(investisseur);
    }
  }
  formatLabelFromFicheConseil(investisseur: Investisseur): string {
    let label = '';
    label += investisseur.displayName;
    if (investisseur.dateNaissance) {
      if (investisseur.civiliteCode == 'MME') {
        label += ' (née le ';
      } else {
        label += ' (né le ';
      }
      label += formatDate(investisseur.dateNaissance, 'dd/MM/yyyy', 'fr_FR') + ')';
    }
    if (investisseur.creationDate) {
      label += ` - créé le ${formatDate(investisseur.creationDate, 'dd/MM/yyyy', 'fr_FR')}`;
    }
    if (investisseur.statutCodeNavigation?.libelle) {
      label += ' - ' + investisseur.statutCodeNavigation?.libelle.toLocaleUpperCase();
    }
    return label;
  }
  formatLabelFromAtlas(investisseur: Investisseur): string {
    let label = '';
    label += investisseur?.investisseurEntite?.displayName ?? '';
    if (investisseur?.investisseurEntite?.personnePhysique) {
      if (investisseur?.investisseurEntite?.personnePhysique?.dateNaissance) {
        if (investisseur?.investisseurEntite?.personnePhysique?.genre == PERSONNE_PHYSIQUE_FEMALE_GENDER_VALUE) {
          label += ' (née le ';
        } else {
          label += ' (né le ';
        }
        label +=
          formatDate(investisseur.investisseurEntite?.personnePhysique?.dateNaissance, 'dd/MM/yyyy', 'fr_FR') + ')';
      }

      if (investisseur.creationDate) {
        label += ` - créé le ${formatDate(investisseur.creationDate, 'dd/MM/yyyy', 'fr_FR')}`;
      }
      if (investisseur.statutCodeNavigation?.libelle) {
        label += ' - ' + investisseur.statutCodeNavigation?.libelle.toLocaleUpperCase();
      }
    }
    if (investisseur.investisseurConsultant?.length > 0) {
      label +=
        ' - ' +
        investisseur.investisseurConsultant?.[0]?.consultant?.personnePhysique?.[0]?.nom +
        ' ' +
        investisseur.investisseurConsultant?.[0]?.consultant?.personnePhysique?.[0]?.prenom;
      if (investisseur.investisseurConsultant?.length > 1) {
        label += ' (primaire), ';
        label +=
          investisseur.investisseurConsultant?.[1]?.consultant?.personnePhysique?.[0]?.nom +
          ' ' +
          investisseur.investisseurConsultant?.[1]?.consultant?.personnePhysique?.[0]?.prenom;
        label += ' (secondaire)';
      }
    }
    return label;
  }
  setControlValue(investisseurId: number | null | undefined) {
    if (investisseurId) {
      this.investisseurControl.setValue(investisseurId);
    } else {
      this.investisseurControl.setValue(null);
      this.investisseurControl.reset();
      this.investisseurAutocomplete.empty();
    }
    this.investisseurControl.updateValueAndValidity();
  }

  public markAsTouched() {
    this.investisseurControl.markAsTouched();
    this.investisseurControl.updateValueAndValidity();
  }
}
